import React from 'react';
import type { MarkedRegion } from '~/components/pdf-pages-viewer';
import { motion } from 'motion/react';
import * as Headless from '@headlessui/react';
import { ChatBubbleLeftIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Spinner } from '@oysterjs/ui/spinner';

import {
  type CaraWorkspaceFieldsFragment,
  CaraWorkspaceState,
  type HtmlDocumentTemplateFieldsFragment
} from '~/api/graphql';
import { useArtifactEditor } from '~/components/workspaces/hooks/use-artifact-editor';
import { useHtmlDocumentExportStyle } from '~/components/workspaces/hooks/use-html-document-export-style';
import { useWorkspaceAttachableArtifacts } from '~/components/workspaces/hooks/use-workspace-attachable-artifacts';
import { VisibilityPopover } from '~/components/visibility/visibility-popover';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';

import {
  ArtifactVersionPicker,
  VersionChangeBadge
} from './artifact-focus-html-document-version-picker';
import { DocumentDesignControls } from './artifact-focus-html-document-design';
import { DocumentTemplateBadge } from './artifact-focus-html-document-template-badge';
import { Editor } from '~/components/document-editor';
import { getArtifactHtmlDocumentTemplateId } from '~/components/workspaces/lib/html-document-artifact';
import { RenderArtifactButton } from './artifact-focus-html-document-render-button';
import { SaveAsTemplateButton } from './artifact-focus-html-document-save-as-template-button';
import {
  type SaveStatus,
  SaveStatusIndicator
} from '~/components/document-editor/artifact-focus-html-document-save-status-indicator';
import { DocumentCommentsPanel } from './artifact-focus-html-document-comments';
import {
  type ArtifactComment,
  useArtifactComments
} from '~/components/workspaces/hooks/use-artifact-comments';
import { useCommentDraft } from '~/components/workspaces/hooks/use-comment-draft';
import type { CommentHighlight } from '~/components/document-editor/extensions/comment-highlight';
import { usePagedDocumentsEnabled } from '~/components/use-paged-documents-enabled';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { useDocumentDesigns } from '~/components/workspaces/hooks/use-document-designs';
import { useAutomaticDesignRefit } from '~/components/workspaces/hooks/use-automatic-design-refit';
import { documentDesign } from '~/components/workspaces/lib/document-design';
import { DesignRefitNotice } from '~/components/workspaces/design-refit-notice';
import { HtmlDocumentFocus as LegacyHtmlDocumentFocus } from '~/pages/copilot/workspace/legacy-editor/artifact-focus-html-document';

type HtmlDocumentFocusProps = {
  workspace: CaraWorkspaceFieldsFragment;
  canUpdate: boolean;
  artifact: CaraWorkspaceFieldsFragment['artifacts'][0];
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
  onClose: () => void;
  onVersionChange: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
  /** Moves to a version this panel's own change created, once the workspace carries it. */
  onVersionCreated?: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
  /** Takes a version a save returned, so the panel shows it before the next workspace update. */
  onArtifactSaved?: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
  onConvertToTemplate?: () => void;
  isConvertingToTemplate?: boolean;
  autoOpenSaveDialog?: boolean;
  onSaveDialogClosed?: () => void;
  onSaveStatusChange?: (status: SaveStatus) => void;
};

/**
 * Agencies without the paged document get main's focus: no comments, no pages view. Only the
 * paged focus, and only while designs are on, gets the callbacks a design change needs, so with
 * either flag off a focus is handed exactly what main hands it.
 */
export const HtmlDocumentFocus = ({
  onVersionCreated,
  onArtifactSaved,
  ...props
}: HtmlDocumentFocusProps) => {
  const designsOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  return usePagedDocumentsEnabled() ? (
    <PagedHtmlDocumentFocus {...props} {...(designsOn && { onVersionCreated, onArtifactSaved })} />
  ) : (
    <LegacyHtmlDocumentFocus {...props} />
  );
};

const PagedHtmlDocumentFocus = (props: HtmlDocumentFocusProps) => {
  const { artifact } = props;
  const designsOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  const {
    saveStatus,
    setSaveStatus,
    staleVersionMessage,
    handleUpdate,
    handleTitleUpdate,
    handleVisibilityUpdate
  } = useArtifactEditor({
    workspaceId: props.workspace.id,
    artifactId: artifact.id,
    version: artifact.version
  });
  const visibilityEditAllowed = useVisibilityEditAllowed(props.workspace.agencyUser?.id);

  const onSaveStatusChange = props.onSaveStatusChange;
  React.useEffect(() => {
    onSaveStatusChange?.(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  // A document opens as the client will see it. Editing is one click away.
  const [showPreview, setShowPreview] = React.useState(true);
  React.useEffect(() => {
    setShowPreview(true);
  }, [artifact.id]);

  // Inline title editing: local value synced from artifact
  const [titleValue, setTitleValue] = React.useState(artifact.document?.title ?? '');
  React.useEffect(() => {
    setTitleValue(artifact.document?.title ?? '');
  }, [artifact.id, artifact.version, artifact.document?.title]);

  const versionsOfCurrentArtifact = React.useMemo(
    () =>
      props.workspace.artifacts
        .filter((a) => a.id === artifact.id)
        .sort((a, b) => b.version - a.version),
    [props.workspace.artifacts, artifact.id]
  );
  const onLatestVersion = artifact.version >= (versionsOfCurrentArtifact[0]?.version ?? 0);

  // A reader on the latest version moves with Cara onto the next one; a reader who chose an
  // earlier version is not pulled off it. Moving remounts the editor, which drops text that is not
  // saved, so the move waits out a save in flight and does not happen while a save stands refused:
  // the reader keeps their text and the refusal, whose Open latest version takes them forward.
  const latest = versionsOfCurrentArtifact[0];
  const seenLatestVersion = React.useRef(latest?.version);
  const onVersionChange = props.onVersionChange;
  React.useEffect(() => {
    const previous = seenLatestVersion.current;
    if (!latest || previous === undefined || latest.version <= previous) {
      seenLatestVersion.current = latest?.version;
      return;
    }
    if (artifact.version === previous) {
      if (saveStatus === 'saving' || saveStatus === 'error' || staleVersionMessage) return;
      onVersionChange(latest);
    }
    seenLatestVersion.current = latest.version;
  }, [latest, artifact.version, saveStatus, staleVersionMessage, onVersionChange]);

  const { workspaceFiles, workspaceMessageFiles, workspaceArtifacts } =
    useWorkspaceAttachableArtifacts(props.workspace);

  const { selectedStyleId, setSelectedStyleId, previewStyleId } =
    useHtmlDocumentExportStyle(artifact);

  const editable = props.canUpdate && props.workspace.state !== CaraWorkspaceState.Working;
  // The server refuses a save to any version but the latest. It takes comments on any version.
  const writable = editable && onLatestVersion;
  const openLatest = latest && !onLatestVersion ? () => onVersionChange(latest) : undefined;
  // An earlier version opens on its pages, where Open latest version stands in for Edit. Keyed on
  // the version alone, so a newer version landing does not hide text the reader is typing.
  React.useEffect(() => {
    if (!onLatestVersion) setShowPreview(true);
  }, [artifact.version]);
  // A final version keeps the words it was marked with; the server refuses edits to them.
  const final = !!artifact.document?.final;
  React.useEffect(() => {
    if (final) setShowPreview(true);
  }, [final]);
  const comments = useArtifactComments({
    workspaceId: props.workspace.id,
    artifactId: artifact.id,
    artifactVersion: artifact.version
  });
  const [commentsOpen, setCommentsOpen] = React.useState<boolean | null>(null);
  const {
    draft,
    setDraft,
    body: draftBody,
    setBody: setDraftBody
  } = useCommentDraft(props.workspace.id, artifact.id, artifact.version);
  const commentsVisible =
    commentsOpen ?? (!!draft || comments.comments.length > 0 || !!comments.error);
  const [activeCommentId, setActiveCommentId] = React.useState<string | null>(null);
  const [reveal, setReveal] = React.useState<{ highlight: CommentHighlight; nonce: number } | null>(
    null
  );
  React.useEffect(() => {
    setCommentsOpen(null);
    setReveal(null);
  }, [artifact.id, artifact.version]);
  React.useEffect(() => setActiveCommentId(null), [artifact.id]);
  const draftStyleId = draft?.kind === 'region' ? draft.region.styleId : undefined;
  React.useEffect(() => {
    if (draft?.kind === 'region') setSelectedStyleId(draftStyleId ?? null);
  }, [artifact.id, artifact.version, draft?.kind, draftStyleId, setSelectedStyleId]);
  const activeRegion = comments.comments.find((c) => c.id === activeCommentId)?.region;
  React.useEffect(() => {
    if (activeRegion?.version === artifact.version) {
      setSelectedStyleId(activeRegion.styleId ?? null);
    }
  }, [
    activeCommentId,
    activeRegion?.version,
    activeRegion?.styleId,
    artifact.version,
    setSelectedStyleId
  ]);
  const highlights = React.useMemo<CommentHighlight[]>(
    () =>
      comments.open.map((c) => ({
        id: c.id,
        quote: c.placement?.matchedText ?? c.anchor.quote,
        occurrence: c.placement?.occurrence ?? c.anchor.occurrence
      })),
    [comments.open]
  );
  const commentRegions = React.useMemo<MarkedRegion[]>(() => {
    const saved: MarkedRegion[] = comments.open.flatMap((c) => {
      if (!c.region) return [];
      // Coordinates describe the version and style on which the box was drawn.
      if (
        c.region.version !== artifact.version ||
        c.region.revision !== artifact.revision ||
        (c.region.styleId ?? null) !== (previewStyleId ?? null)
      )
        return [];
      const { page, left, top, width, height } = c.region;
      return [{ id: c.id, page, left, top, width, height, active: c.id === activeCommentId }];
    });
    // The box being written stays on the page until the comment is added or dropped.
    if (draft?.kind === 'region' && (draft.region.styleId ?? null) === (previewStyleId ?? null)) {
      const { page, left, top, width, height } = draft.region;
      saved.push({ id: 'draft', page, left, top, width, height, active: true });
    }
    return saved;
  }, [comments.open, activeCommentId, draft, artifact.version, artifact.revision, previewStyleId]);
  const selectComment = (comment: ArtifactComment) => {
    setActiveCommentId(comment.id);
    if (comment.region) {
      const source = versionsOfCurrentArtifact.find((a) => a.version === comment.region?.version);
      if (source && source.version !== artifact.version) props.onVersionChange(source);
      setSelectedStyleId(comment.region.styleId ?? null);
      setShowPreview(true);
      return;
    }
    setShowPreview(false);
    setReveal({
      highlight: {
        id: comment.id,
        quote: comment.placement?.matchedText ?? comment.anchor.quote,
        occurrence: comment.placement?.occurrence ?? comment.anchor.occurrence
      },
      nonce: Date.now()
    });
  };

  const versionKey = `${artifact.id}:${artifact.version}`;
  const [renderedPages, setRenderedPages] = React.useState<{
    versionKey: string;
    declared: number;
    printed: number;
  } | null>(null);
  const onPageCount = React.useCallback(
    (pages: { declared: number; printed: number }) => setRenderedPages({ versionKey, ...pages }),
    [versionKey]
  );
  const rendered = renderedPages?.versionKey === versionKey ? renderedPages : null;
  const pageCheck =
    artifact.document?.pageCheck ??
    (rendered && rendered.declared > 0 && rendered.printed > rendered.declared
      ? { declaredPages: rendered.declared, printedPages: rendered.printed }
      : null);

  const htmlDocumentTemplateID = getArtifactHtmlDocumentTemplateId(artifact);
  const templateLabel = htmlDocumentTemplateID
    ? props.htmlDocumentTemplates.find((t) => t.id === htmlDocumentTemplateID)?.title
    : null;
  const design = documentDesign(artifact, props.htmlDocumentTemplates);
  const designChoices = useDocumentDesigns();
  const unsavedTitle = titleValue !== (artifact.document?.title ?? '');
  const unsaved = unsavedTitle || saveStatus === 'saving' || saveStatus === 'error';
  const designRefit = useAutomaticDesignRefit({
    artifactId: artifact.id,
    version: artifact.version,
    enabled: designsOn && writable && !final && !design.styleLinked && !staleVersionMessage,
    unsaved,
    fitState: artifact.designFitState,
    onRefitted: (saved) => (props.onVersionCreated ?? props.onVersionChange)(saved)
  });
  const updatingDesign = designRefit.updating;
  const contentEditable = writable && !final && !updatingDesign;
  // The save fails for a design the caller cannot read or one that has been archived, and the
  // choices list only readable, unarchived designs.
  const templateDesignId = designChoices.choices?.designs.some(
    (each) => each.id === design.designId
  )
    ? design.designId
    : null;

  return (
    <motion.div className="h-full flex flex-col">
      <div className="flex flex-row items-center gap-2">
        <Headless.Button
          className="rounded-full -ml-3 py-1.5 pl-2 pr-3 flex items-center justify-center gap-2 hover:bg-neutral-200/65 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white transition-all duration-150 cursor-pointer"
          onClick={props.onClose}
        >
          <ChevronLeftIcon className="size-4" />
          <div className="text-xs">Close editor</div>
        </Headless.Button>
      </div>
      {/* Row 1: title, Save as template, and Open PDF (split) */}
      <div className="mt-0.5 flex flex-row items-center justify-between gap-4 min-w-0">
        <input
          value={titleValue}
          readOnly={!writable || updatingDesign}
          onChange={(e) => setTitleValue(e.target.value)}
          onBlur={() => {
            if (writable && !updatingDesign) handleTitleUpdate(titleValue);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          className="min-w-0 flex-1 bg-transparent border-none outline-none text-lg leading-tight font-medium text-neutral-900 dark:text-white truncate placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:ring-0 p-0"
          placeholder="Untitled"
          aria-label="Document title"
        />
        <div className="flex items-center gap-2 shrink-0">
          {props.canUpdate && (
            <SaveAsTemplateButton
              artifactContent={artifact.document?.content || ''}
              artifactTitle={artifact.document?.title}
              onConvertRequest={props.onConvertToTemplate}
              externalOpen={props.autoOpenSaveDialog}
              externalInitialStep={2}
              externalInitialMethod="convert"
              onExternalClose={props.onSaveDialogClosed}
              artifactWasConverted={!!artifact.document?.sourceFileId}
              designId={designsOn ? (templateDesignId ?? undefined) : undefined}
            />
          )}
          <RenderArtifactButton
            artifact={artifact}
            workspaceId={props.workspace.id}
            htmlDocumentTemplates={props.htmlDocumentTemplates}
            workspaceArtifacts={workspaceArtifacts}
            workspaceFiles={workspaceFiles}
            workspaceMessageFiles={workspaceMessageFiles}
            selectedStyleId={selectedStyleId}
            onSelectedStyleIdChange={setSelectedStyleId}
            refitDesign={designsOn && writable && !final && !design.styleLinked && !unsaved}
            disabled={updatingDesign}
          />
          {writable && visibilityEditAllowed && (
            <VisibilityPopover visibility={artifact.visibility} onSave={handleVisibilityUpdate} />
          )}
        </div>
      </div>
      {/* Row 2: version + provenance on left; save status. The left side wraps within itself so
          Comments keeps its place at the end of the first line. */}
      <div className="mt-1.5 flex items-center justify-between gap-2">
        {(designRefit.failed || designRefit.pending) && (
          <DesignRefitNotice pending={designRefit.pending} onRetry={designRefit.retry} />
        )}
        <div className="flex min-w-0 flex-1 items-center gap-2 flex-wrap">
          <ArtifactVersionPicker
            versions={versionsOfCurrentArtifact}
            allArtifacts={props.workspace.artifacts}
            currentArtifact={artifact}
            onVersionChange={props.onVersionChange}
          />
          <DocumentDesignControls
            artifact={artifact}
            design={design}
            choices={designChoices.choices}
            onDesignsOpen={designChoices.refresh}
            editable={editable}
            updating={updatingDesign}
            latest={onLatestVersion}
            saveStatus={saveStatus}
            onVersionChange={props.onVersionCreated ?? props.onVersionChange}
            onArtifactSaved={(saved) => props.onArtifactSaved?.(saved)}
          >
            {!!htmlDocumentTemplateID && !!templateLabel && (
              <DocumentTemplateBadge
                htmlDocumentTemplateId={htmlDocumentTemplateID}
                templateTitle={templateLabel}
                control
              />
            )}
          </DocumentDesignControls>
          <VersionChangeBadge artifact={artifact} versions={versionsOfCurrentArtifact} />
          {pageCheck && <Badge color="amber">{pagesNote(pageCheck)}</Badge>}
          {props.workspace.state !== CaraWorkspaceState.Working && (
            <SaveStatusIndicator
              status={saveStatus}
              staleVersionMessage={staleVersionMessage}
              onOpenLatest={openLatest}
            />
          )}
        </div>
        <Headless.Button
          onClick={() => setCommentsOpen(!commentsVisible)}
          aria-label="Comments"
          title="Comments"
          className="flex shrink-0 items-center gap-1.5 rounded-full p-1.5 text-xs text-neutral-700 hover:bg-neutral-200/65 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/10 dark:hover:text-white"
        >
          <ChatBubbleLeftIcon className="size-4" />
          {comments.open.length > 0 && <Badge color="amber">{comments.open.length}</Badge>}
        </Headless.Button>
      </div>
      {props.isConvertingToTemplate && <TemplateProgressBanner />}
      <div className="flex flex-row gap-3 flex-grow min-h-0">
        <div className="flex flex-col flex-grow min-w-0 min-h-0">
          <Editor
            key={`${artifact.id}-${artifact.version}`}
            content={artifact.document?.content || ''}
            editable={contentEditable}
            readOnlyNote={
              updatingDesign
                ? 'Updating to your Design…'
                : editable && final
                  ? 'Read-only'
                  : undefined
            }
            onOpenLatest={openLatest}
            onPageCount={onPageCount}
            onUpdate={handleUpdate}
            onSaveStatusChange={setSaveStatus}
            workspaceId={props.workspace.id}
            artifactId={artifact.id}
            artifactVersion={artifact.version}
            artifactRevision={
              designRefit.renderRevision
                ? `${artifact.revision}:${designRefit.renderRevision}`
                : artifact.revision
            }
            templateId={previewStyleId}
            showPreview={showPreview}
            onPreviewToggle={() => setShowPreview(!showPreview)}
            onCommentSelection={
              editable
                ? (candidate) => {
                    setDraft({ kind: 'text', candidate });
                    setCommentsOpen(true);
                  }
                : undefined
            }
            commentRegions={commentRegions}
            onDrawCommentRegion={
              editable
                ? (region) => {
                    setDraft({
                      kind: 'region',
                      region: { ...region, styleId: previewStyleId, revision: artifact.revision }
                    });
                    setCommentsOpen(true);
                  }
                : undefined
            }
            onCommentRegionClick={(id) => {
              if (id === 'draft') return;
              const comment = comments.open.find((c) => c.id === id);
              if (comment) {
                setActiveCommentId(comment.id);
                setCommentsOpen(true);
              }
            }}
            commentHighlights={highlights}
            activeCommentId={activeCommentId}
            revealComment={reveal}
          />
        </div>
        {commentsVisible && (
          <DocumentCommentsPanel
            comments={comments.comments}
            currentVersion={artifact.version}
            currentRevision={artifact.revision}
            draft={draft}
            body={draftBody}
            onBodyChange={setDraftBody}
            activeId={activeCommentId}
            busy={comments.busy}
            loading={comments.loading}
            error={comments.error}
            canEdit={editable}
            latest={onLatestVersion}
            onAdd={async (body) => {
              if (draft && (await comments.add(draft, body))) setDraft(null);
            }}
            onCancelDraft={() => setDraft(null)}
            onSelect={selectComment}
            onResolve={comments.resolve}
            onDelete={comments.remove}
            onSend={comments.send}
            onRetry={comments.retry}
            onClose={() => setCommentsOpen(false)}
          />
        )}
      </div>
    </motion.div>
  );
};

const pages = (count: number) => `${count} page${count === 1 ? '' : 's'}`;

/** What Cara's review left unresolved: the pages the document plans against the sheets it prints. */
const pagesNote = (check: { declaredPages: number; printedPages: number }) =>
  `Planned ${pages(check.declaredPages)}, prints ${check.printedPages}`;

const TemplateProgressBanner = () => (
  <div className="mt-2 rounded-lg bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/30 px-3 py-2">
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm font-medium text-neutral-900 dark:text-white">
        Building template
      </span>
      <Spinner size="size-3.5" color="sky" />
    </div>
    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
      Cara is replacing variable content with placeholders…
    </p>
  </div>
);
